import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTransactionMutations } from '../components/transactions/hooks/useTransactionMutations';
import useDocumentFocus from '../hooks/useDocumentFocus';
import { RecalculateData } from '../services/transaction.types';
import { useTransactionService } from '../services/transaction.service';
import { QUERY_KEYS } from '../utils/queryKeys';
import { useAuth } from './AuthContext';

interface RecalculationStatusContextProps {
    status: RecalculateData | null;
}

const RecalculationStatusContext = createContext<RecalculationStatusContextProps | undefined>(undefined);

export const RecalculationStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const [status, setStatus] = useState<RecalculateData | null>(null);
    const { startRecalculation } = useTransactionMutations();
    const transactionService = useTransactionService();
    const isFocused = useDocumentFocus();
    const { currentCtUser } = useAuth();
    const location = useLocation();

    // Smart query invalidation based on current page
    const invalidateQueriesStaggered = async () => {
        const currentPath = location.pathname;
        
        if (currentPath.includes('/tax-reports')) {
            // Tax reports page: prioritize tax reports, then other queries
            QUERY_KEYS.taxQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Invalidate remaining queries
            QUERY_KEYS.transactionsQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            await new Promise(resolve => setTimeout(resolve, 1200));
            QUERY_KEYS.dataQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            
        } else if (currentPath.includes('/transactions')) {
            // Transactions page: prioritize transactions, then other queries
            QUERY_KEYS.transactionsQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Invalidate remaining queries
            QUERY_KEYS.taxQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            await new Promise(resolve => setTimeout(resolve, 1200));
            QUERY_KEYS.dataQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            
        } else {
            // Other pages: tax reports first, then transactions, then rest
            QUERY_KEYS.taxQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            await new Promise(resolve => setTimeout(resolve, 200));
            QUERY_KEYS.transactionsQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
            await new Promise(resolve => setTimeout(resolve, 1200));
            
            // Invalidate remaining queries
            QUERY_KEYS.dataQueries().forEach(key => {
                queryClient.invalidateQueries({ queryKey: [key] });
            });
        }
    };


    useQuery({
        queryKey: [QUERY_KEYS.RECALCULATION],
        queryFn: async () => {
            try {
                const data = await transactionService.recalculateInfo();
                
                if (status?.run && !data.run) {
                    // Recalculation just finished - use smart staggered invalidation
                    console.log(`🔄 Recalculation finished at ${new Date().toLocaleTimeString()}! Starting smart query invalidation...`);
                    invalidateQueriesStaggered();
                }

                setStatus(data);

                if (!data.run && data.new) {
                    // Should be running but isn't, start it
                    console.log(`🚀 Recalculation started at ${new Date().toLocaleTimeString()}`);
                    startRecalculation();
                }
                
                return data;
            } catch (error) {
                console.error('Failed to check recalculation status:', error);
                throw error;
            }
        },
        enabled: !!currentCtUser,
    });

    useQuery({
        queryKey: ['recalculateInfoInterval'],
        queryFn: async () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
            return null;
        },
        refetchInterval: 30000,
        enabled: ((isFocused && status?.run) || status === null) && !!currentCtUser,
    });

    const value = {
        status
    };

    return (
        <RecalculationStatusContext.Provider value={value}>
            {children}
        </RecalculationStatusContext.Provider>
    );
};

export const useRecalculation = (): RecalculationStatusContextProps => {
    const context = useContext(RecalculationStatusContext);
    if (context === undefined) {
        throw new Error('useRecalculation must be used within a RecalculationStatusProvider');
    }
    return context;
};
